import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type JourneyListItem,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { HelpTip } from '../components/HelpTip';
import { StatusBadge } from '../components/StatusBadge';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import { journeyTypeLabel } from './journeyLabels';
import styles from './JourneysList.module.css';

type LoadState = 'loading' | 'error' | 'ready';

const CRITICALITY_LABELS: Record<string, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export interface JourneysListProps {
  client?: ApiClient;
}

/**
 * Project journeys (#133, slice 1). Lists the current journey per (type, source)
 * from GET …/journeys with a source badge distinguishing customer-provided from
 * discovered. Manual Refresh, no polling (mirrors ScansList). The authoring
 * create/edit surface is wired in the following slices.
 */
export function JourneysList({ client = defaultClient }: JourneysListProps) {
  const { currentOrg } = useOrg();
  const { id } = useParams<{ id: string }>();
  useFocusMain();

  const orgId = currentOrg?.id;

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [journeys, setJourneys] = useState<JourneyListItem[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!orgId || !id) return;
    setPhase('loading');
    try {
      const result = await client.listJourneys(orgId, id);
      setJourneys(result.journeys);
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    if (!orgId || !id || refreshing) return;
    setRefreshing(true);
    try {
      const result = await client.listJourneys(orgId, id);
      setJourneys(result.journeys);
      setStatusMessage('Journeys updated.');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    } finally {
      setRefreshing(false);
    }
  }, [client, orgId, id, refreshing]);

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 id="journeys-heading" className={styles.heading}>
          Journeys
        </h1>
        <p className={styles.subheading}>
          Customer-provided and discovered journeys for this project.{' '}
          <HelpTip term="journey" />
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading journeys…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {phase === 'ready' ? (
          <>
            <InlineAlert severity="success">{statusMessage}</InlineAlert>

            <div className={styles.actions}>
              <Link to={`/projects/${id ?? ''}/journeys/new`} className={styles.createLink}>
                Create journey
              </Link>
              <Button variant="secondary" busy={refreshing} onClick={refresh}>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
              <Link to={`/projects/${id ?? ''}/journey-health`} className={styles.backLink}>
                View journey health
              </Link>
            </div>

            {journeys.length === 0 ? (
              <p className={styles.empty}>
                No journeys yet. TestCraft discovers journeys automatically when you run a scan, or
                you can add one yourself with &ldquo;Create journey&rdquo; above.
              </p>
            ) : (
              <table className={styles.table} aria-labelledby="journeys-heading">
                <thead>
                  <tr>
                    <th scope="col">Journey</th>
                    <th scope="col">Type</th>
                    <th scope="col">Source</th>
                    <th scope="col">Criticality</th>
                    <th scope="col">Steps</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {journeys.map((journey) => (
                    <tr key={journey.id}>
                      <td>{journey.title}</td>
                      <td>{journeyTypeLabel(journey.journeyType)}</td>
                      <td>
                        <StatusBadge kind="source" status={journey.source} />
                      </td>
                      <td>{CRITICALITY_LABELS[journey.criticality] ?? journey.criticality}</td>
                      <td>{journey.stepCount}</td>
                      <td>
                        {journey.source === 'customer-provided' ? (
                          <Link
                            to={`/projects/${id ?? ''}/journeys/${journey.id}/edit`}
                            aria-label={`Edit ${journey.title}`}
                          >
                            Edit
                          </Link>
                        ) : (
                          <span aria-hidden="true">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        ) : null}
      </Card>
    </AppShell>
  );
}
